/**
 * ticketflow/ConsoleSauvegarde — sauvegarde et migration de la CONSOLE elle-même
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── CE QUE CET ÉCRAN SAUVEGARDE, ET CE QU'IL NE SAUVEGARDE PAS ──────────────
 *
 * PAS la configuration d'un site : celle-là se capture depuis le magasin, site par site.
 * Ici, c'est la CONSOLE — son identité d'éditeur, ses sites, son enrôlement, son magasin.
 *
 * Déplacer la console n'est pas une réinstallation, c'est un TRANSFERT D'IDENTITÉ. Chaque
 * `.exe` déjà livré vérifie les ordres de l'éditeur contre la clé publique qu'il embarque :
 * une console repartie sur une paire de clés neuve est, pour tout le parc, un inconnu, et
 * le seul remède serait de réinstaller un `.exe` chez chaque client.
 *
 * ─── POURQUOI LA PHRASE DE PASSE N'EST PAS OPTIONNELLE ───────────────────────
 *
 * L'archive contient la clé privée de signature, le mot de passe de la console et les
 * jetons de tous les sites. Qui la lit devient l'éditeur. Elle est donc chiffrée
 * (AES-256-GCM, clé dérivée par scrypt) et la phrase est exigée à la création comme à la
 * relecture. Perdue, l'archive est irrécupérable — c'est le prix de la protection, et il
 * est dit ici, pas découvert le jour de la restauration.
 */

import React, { useState } from 'react';

const LONGUEUR_MINIMALE = 12;

const poster = async (url, corps) => {
  const r = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corps),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || `Échec (HTTP ${r.status})`);
  return j.data;
};

export default function ConsoleSauvegarde() {
  const [phrase, setPhrase] = useState('');
  const [phraseRestauration, setPhraseRestauration] = useState('');
  const [fichier, setFichier] = useState(null);
  const [statut, setStatut] = useState('');
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState('');

  const trop_courte = phrase.length > 0 && phrase.length < LONGUEUR_MINIMALE;

  const sauvegarder = async () => {
    setErreur(''); setStatut(''); setOccupe('sauvegarde');
    try {
      const archive = await poster('/api/console/sauvegarde', { phrase });
      // Téléchargement immédiat : une archive qui resterait sur la console ne protégerait
      // de rien — c'est précisément la machine dont on se prémunit de la perte.
      const blob = new Blob([JSON.stringify(archive, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const t = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `console-ticketflow-${t}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setStatut('✓ Archive téléchargée. Conservez-la AILLEURS que sur cette machine.');
      setPhrase('');
    } catch (e) {
      setErreur(e.message);
    } finally { setOccupe(''); }
  };

  const restaurer = async () => {
    setErreur(''); setStatut('');
    if (!fichier) { setErreur('Choisissez une archive.'); return; }
    if (!window.confirm(
      'Restaurer cette archive ?\n\nElle REMPLACE les sites, l\'enrôlement et le magasin de '
      + 'cette console, et réécrit ses secrets. Un redémarrage sera nécessaire.',
    )) return;
    setOccupe('restauration');
    try {
      const archive = JSON.parse(await fichier.text());
      const r = await poster('/api/console/restauration', { archive, phrase: phraseRestauration });
      setStatut(
        `✓ Restauré : ${r.applique.join(', ')}.`
        + (r.redemarrageRequis
          // On le DIT, parce que rien à l'écran ne le laisserait deviner : la clé de
          // signature est lue AU DÉMARRAGE. Tant qu'il n'a pas eu lieu, la console signe
          // encore avec l'ancienne identité — ou avec aucune.
          ? ' ⚠ REDÉMARRAGE REQUIS (pm2 restart ticket-admin) pour que la clé de signature prenne effet.'
          : ''),
      );
      setPhraseRestauration('');
    } catch (e) {
      setErreur(e.message);
    } finally { setOccupe(''); }
  };

  return (
    <div className="console-sauvegarde" style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0 }}>Sauvegarde de la console</h2>
      <p className="muted">
        Cette archive contient ce qui fait que cette console est <b>votre</b> console : sa clé
        de signature, ses accès, ses sites et leurs jetons, l'enrôlement, et le magasin.
        Elle ne contient <b>pas</b> la configuration des sites — celle-là se capture article
        par article depuis le magasin.
      </p>

      <div style={{ background: 'rgba(220,38,38,.08)', border: '1px solid rgba(220,38,38,.35)', borderRadius: 8, padding: '10px 12px', margin: '14px 0' }}>
        <b>À lire une fois.</b> Sans la clé de signature, une console remontée ailleurs est
        <b> inconnue de tous les serveurs déjà livrés</b> : ils rejettent ses ordres, et il
        faudrait réinstaller un exécutable chez chaque client. Cette archive est le seul moyen
        de l'emporter. Qui la lit devient l'éditeur : d'où le chiffrement obligatoire.
      </div>

      {statut && <div className="status" style={{ background: 'rgba(22,163,74,.12)' }}>{statut}</div>}
      {erreur && <div className="status" style={{ background: 'rgba(220,38,38,.12)' }}>{erreur}</div>}

      <section style={{ marginTop: 18 }}>
        <h3>Créer une archive</h3>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '8px 0 4px' }}>
          Phrase de passe ({LONGUEUR_MINIMALE} caractères minimum)
        </label>
        <input
          type="password"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder="une phrase que vous retrouverez dans un an"
          style={{ width: '100%', maxWidth: 420, padding: '8px 10px' }}
        />
        {trop_courte && <div className="muted" style={{ color: '#dc2626', fontSize: 12 }}>Trop courte : {phrase.length}/{LONGUEUR_MINIMALE}.</div>}
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Notez-la ailleurs : sans elle, l'archive est définitivement illisible.
        </div>
        <div style={{ marginTop: 10 }}>
          <button onClick={sauvegarder} disabled={phrase.length < LONGUEUR_MINIMALE || occupe === 'sauvegarde'}>
            {occupe === 'sauvegarde' ? '… chiffrement' : '⤓ Créer et télécharger'}
          </button>
        </div>
      </section>

      <section style={{ marginTop: 26 }}>
        <h3>Restaurer / migrer vers cette console</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          À faire sur la machine d'ACCUEIL. Les réglages qui lui sont propres (port, chemins)
          sont conservés — la restauration fusionne, elle n'écrase pas en bloc. Elle est
          rejouable : la relancer donne le même résultat.
        </p>
        <input type="file" accept="application/json,.json" onChange={(e) => setFichier(e.target.files?.[0] || null)} />
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '10px 0 4px' }}>
          Phrase de passe de l'archive
        </label>
        <input
          type="password"
          value={phraseRestauration}
          onChange={(e) => setPhraseRestauration(e.target.value)}
          style={{ width: '100%', maxWidth: 420, padding: '8px 10px' }}
        />
        <div style={{ marginTop: 10 }}>
          <button onClick={restaurer} disabled={!fichier || !phraseRestauration || occupe === 'restauration'} className="danger">
            {occupe === 'restauration' ? '… restauration' : '⤒ Restaurer sur cette console'}
          </button>
        </div>
      </section>

      <section style={{ marginTop: 26 }}>
        <h3>Après une migration</h3>
        <ol className="muted" style={{ paddingLeft: 20, lineHeight: 1.7 }}>
          <li><b>Redémarrer</b> la console — la clé de signature est lue au démarrage.</li>
          <li>Faire pointer le <b>nom DNS</b> vers la nouvelle machine : clé identique + même nom, et tout le parc suit sans qu'on touche à un seul site.</li>
          <li>Vérifier qu'un site remonte « en ligne », puis <b>approuver un écran</b> — c'est ce geste qui prouve que la signature est acceptée.</li>
        </ol>
      </section>
    </div>
  );
}
